import { GetJobsJobTypeEnum, GetJobsProcessTypeEnum, GetJobsTenantEnum, ImportManagementServiceApi } from 'api/import/apis/ImportManagementServiceApi';
import { JobDTO, JobFiltersDTO, PageJobDTO } from 'api/import/models';
import { AuthContext } from 'Auth/components/AuthProvider';
import { getAuthMiddleware } from 'Auth/utils/GetAuthMiddleware';
import { Layout } from 'pages/Layout';
import { addLocale, FilterMatchMode, locale } from 'primereact/api';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { DataTable, DataTablePageEvent, DataTableRowClickEvent, DataTableStateEvent } from 'primereact/datatable';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import * as React from 'react';
import { createRef, PropsWithChildren, ReactNode, RefObject, useContext, useEffect, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { Direction } from '../../../../util/types';
import { Button } from 'primereact/button';
import { PageType } from './AdminJobWrapper';

const initialLazyTableState: DataTablePageEvent = {
    first: 0,
    rows: 10,
    page: 0,
    sortField: undefined,
    sortOrder: undefined,
    filters: {
        processType: {
            value: null,
            matchMode: FilterMatchMode.EQUALS
        },
        tenant: {
            value: null,
            matchMode: FilterMatchMode.EQUALS
        },
        jobType: {
            value: null,
            matchMode: FilterMatchMode.EQUALS
        }
    }
}

interface AdminJobsManagementProps {
    setCurrentPage:  React.Dispatch<React.SetStateAction<PageType>>;
    setJobId: React.Dispatch<React.SetStateAction<number>>;
}

export const AdminJobsManagement: React.FC<PropsWithChildren<AdminJobsManagementProps>> = (props: PropsWithChildren<AdminJobsManagementProps>): ReactNode => {
    const intl: IntlShape = useIntl();
    const {setCurrentPage, setJobId} = props;
    const [loading, setLoading] = useState<boolean>(false);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [jobs, setJobs] = useState<JobDTO[]>([]);
    const [lazyState, setLazyState] = useState<DataTablePageEvent>(initialLazyTableState);
    const toast: RefObject<Toast> = createRef<Toast>();

    const {actions} = useContext(AuthContext);
    const api: ImportManagementServiceApi = new ImportManagementServiceApi().withMiddleware(getAuthMiddleware(actions));

    const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    } as const;

    const [processTypeOptions, setProcessTypeOptions] = useState<{ label: string; value: any; }[]>([]);
    const [tenantOptions, setTenantOptions] = useState<{ label: string; value: any; }[]>([]);
    const [jobTypeOptions, setJobTypeOptions] = useState<{ label: string; value: any; }[]>([]);

    useEffect((): void => {
        void lazyLoadData(lazyState);
    }, [lazyState]);

    useEffect((): void => {
        void loadFilters();
    }, []);

    useEffect(() => {
        const currentLocale = intl.locale;

        addLocale(currentLocale, {
            clear: intl.formatMessage({ id: 'importManagement.filter.clear' }),
            apply: intl.formatMessage({ id: 'importManagement.filter.apply' }),
        });

        locale(currentLocale);
    }, [intl]);

    const loadFilters = (): void => {
        setLoading(true);

        void api.
            getJobsFilters()
            .then((filters: JobFiltersDTO): void => {
                if(filters) {
                    console.log(filters)
                    setProcessTypeOptions([
                        { label: intl.formatMessage({id: 'importManagement.filter.all'}), value: null },
                        ...filters.processTypes.map(processType => ({
                            label: processType,
                            value: processType,
                        })),
                    ]);

                    setTenantOptions([
                        { label: intl.formatMessage({id: 'importManagement.filter.all'}), value: null },
                        ...filters.tenants.map(tenant => ({
                            label: tenant,
                            value: tenant,
                        })),
                    ]);

                    setJobTypeOptions([
                        { label: intl.formatMessage({id: 'importManagement.filter.all'}), value: null },
                        ...filters.jobTypes.map(jobType => ({
                            label: jobType,
                            value: jobType,
                        })),
                    ]);
                }
                setLoading(false);
            })
            .catch((): void => {
                setLoading(false);
                toast.current.show({severity: 'error', summary: 'Error', detail: 'Loading jobs filters failed'});
            });
    };

    const lazyLoadData = (dataTablePageEvent: DataTablePageEvent): void => {
        setLoading(true);

        const processTypeFilter = dataTablePageEvent.filters?.processType;

        const processType: GetJobsProcessTypeEnum =
            processTypeFilter &&
            !Array.isArray(processTypeFilter) &&
            'value' in processTypeFilter
                ? processTypeFilter.value as GetJobsProcessTypeEnum | null
                : null;

        const tenantFilter = dataTablePageEvent.filters?.tenant;

        const tenant: GetJobsTenantEnum =
            tenantFilter &&
            !Array.isArray(tenantFilter) &&
            'value' in tenantFilter
                ? tenantFilter.value as GetJobsTenantEnum | null
                : null;

        const jobTypeFilter = dataTablePageEvent.filters?.jobType;

        const jobType: GetJobsJobTypeEnum =
            jobTypeFilter &&
            !Array.isArray(jobTypeFilter) &&
            'value' in jobTypeFilter
                ? jobTypeFilter.value as GetJobsJobTypeEnum | null
                : null;

        void api.
            getJobs({
                page: dataTablePageEvent.page,
                size: dataTablePageEvent.rows,
                sort: dataTablePageEvent.sortField || undefined,
                direction: dataTablePageEvent.sortOrder > 0 ? Direction.ASC : Direction.DESC,
                processType: processType ?? undefined,
                tenant: tenant ?? undefined,
                jobType: jobType ?? undefined
            })
            .then((jobsDTOs: PageJobDTO): void => {
                if(jobsDTOs) {
                    setJobs(jobsDTOs.content ?? []);
                    setTotalRecords(jobsDTOs.totalElements);
                }
                setLoading(false);
            })
            .catch((): void => {
                setLoading(false);
                toast.current.show({severity: 'error', summary: 'Error', detail: 'Loading jobs failed'});
            });
    };

    const onPageChange = (e: DataTableStateEvent): void => setLazyState(e);

    const onSortChange = (e: DataTableStateEvent): void => setLazyState(e);

    const onFilterChange = (e: DataTableStateEvent): void => setLazyState({...e, first: 0});

    const processTypeFilter = (
        options: ColumnFilterElementTemplateOptions
    ): React.ReactNode => (
        <Dropdown
            value={options.value ?? null}
            options={processTypeOptions}
            optionLabel='label'
            optionValue='value'
            onChange={(e: DropdownChangeEvent) => options.filterCallback(e.value)}
            showClear
            style={{ width: '100%' }}
        />
    );

    const tenantFilter = (
        options: ColumnFilterElementTemplateOptions
    ): React.ReactNode => (
        <Dropdown
            value={options.value ?? null}
            options={tenantOptions}
            optionLabel='label'
            optionValue='value'
            onChange={(e: DropdownChangeEvent) => options.filterCallback(e.value)}
            showClear
            style={{ width: '100%' }}
        />
    );

    const jobTypeFilter = (
        options: ColumnFilterElementTemplateOptions
    ): React.ReactNode => (
        <Dropdown
            value={options.value ?? null}
            options={jobTypeOptions}
            optionLabel='label'
            optionValue='value'
            onChange={((e: DropdownChangeEvent) => options.filterCallback(e.value))}
            showClear
            style={{ width: '100%' }}
        />
    );

    const header = (
        <div className='flex justify-content-end my-3 gap-2'>
            <Button
                label={intl.formatMessage({id: 'importManagement.button.removeFilters'})}
                onClick={() => setLazyState(initialLazyTableState)}
                icon="pi pi-undo"
                disabled={
                    lazyState.filters?.processType.value === null &&
                    lazyState.filters?.tenant.value === null &&
                    lazyState.filters?.jobType.value === null
                }
            />
            <Button
                label={intl.formatMessage({id: 'importJobsManagement.button.showAll'})}
                onClick={() => setCurrentPage('JOB_ENTRIES')}
                icon="pi pi-arrow-right"
                iconPos="right"
            />
        </div>
    )

    return (
        <Layout>
            <Toast ref={toast}/>
            <ConfirmDialog/>
            <h1 style={{fontSize: 'calc(15px + 1.8vw)'}} className={'md:text-3xl m-1 p-1'}>
                {intl.formatMessage({id: 'importJobsManagement.title'})}
            </h1>
            <DataTable
                value={jobs}
                lazy={true}
                stripedRows={true}
                showGridlines={true}
                dataKey='id'
                paginator={true}
                first={lazyState.first}
                totalRecords={totalRecords}
                sortField={lazyState.sortField}
                sortOrder={lazyState.sortOrder}
                filters={lazyState.filters}
                loading={loading}
                rowsPerPageOptions={[10, 25, 50]}
                rows={lazyState.rows}
                header={header}
                editMode='row'
                onPage={onPageChange}
                onSort={onSortChange}
                onFilter={onFilterChange}
                tableStyle={{
                    width: '100%',
                    tableLayout: 'fixed'
                }}
                rowHover={true}
                style={{ cursor: 'pointer' }}
                onRowClick={(event: DataTableRowClickEvent) => {
                    setCurrentPage('JOB_ENTRIES');
                    setJobId(event.data.id);
                }}
            >
                <Column field='id' style={{width: '10%'}}
                    bodyStyle={{
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word'
                    }}
                    header={intl.formatMessage({id: 'importJobsManagement.header.id'})} sortable
                />
                <Column field='startTime' style={{width: '18%'}}
                    bodyStyle={{
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word'
                    }}
                    header={intl.formatMessage({id: 'importJobsManagement.header.startTime'})}
                    body={(rowData: JobDTO) =>
                        rowData.startTime ? (
                            <>{rowData.startTime.toLocaleDateString("de-DE", options)}</>
                        ) : (<></>)
                    }
                />
                <Column field='processType' style={{width: '9%'}}
                    bodyStyle={{
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word'
                    }}
                    header={intl.formatMessage({id: 'importJobsManagement.header.processType'})}
                    filter
                    filterElement={processTypeFilter}
                    filterMatchMode={FilterMatchMode.EQUALS}
                    showFilterMatchModes={false}
                    filterMenuClassName="import-filter-menu"
                />
                <Column field='tenant' style={{width: '9%'}}
                    bodyStyle={{
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word'
                    }}
                    header={intl.formatMessage({id: 'importJobsManagement.header.tenant'})}
                    filter
                    filterElement={tenantFilter}
                    filterMatchMode={FilterMatchMode.EQUALS}
                    showFilterMatchModes={false}
                    filterMenuClassName="import-filter-menu"
                />
                <Column field='jobType' style={{width: '9%'}}
                    bodyStyle={{
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word'
                    }}
                    header={intl.formatMessage({id: 'importJobsManagement.header.jobType'})}
                    filter
                    filterElement={jobTypeFilter}
                    filterMatchMode={FilterMatchMode.EQUALS}
                    showFilterMatchModes={false}
                    filterMenuClassName="import-filter-menu"
                />
                <Column field='errors' style={{width: '9%'}}
                    bodyStyle={{
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word'
                    }}
                    header={intl.formatMessage({id: 'importJobsManagement.header.errors'})}
                    sortable
                />
                <Column field='successWithError' style={{width: '9%'}}
                    bodyStyle={{
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word'
                    }}
                    header={intl.formatMessage({id: 'importJobsManagement.header.successWithError'})}
                    sortable
                />
                <Column field='success' style={{width: '9%'}}
                    bodyStyle={{
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word'
                    }}
                    header={intl.formatMessage({id: 'importJobsManagement.header.success'})}
                    sortable
                />
            </DataTable>
        </Layout>
    )
}

export default AdminJobsManagement; 